/* eslint-disable */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { doc, serverTimestamp, setDoc, writeBatch } from "firebase/firestore";
import { db } from "../firebase";
import { hapticSelection } from "utils/haptics";
import "./ParentMobile.css";

function BellIcon() {
  return (
    <svg viewBox="0 0 24 24" className="pp-mini-icon" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M15 17h5l-1.4-1.4A2 2 0 0 1 18 14.2V11a6 6 0 1 0-12 0v3.2a2 2 0 0 1-.6 1.4L4 17h5" />
      <path d="M10 17a2 2 0 0 0 4 0" />
    </svg>
  );
}

function MegaphoneIcon() {
  return (
    <svg viewBox="0 0 24 24" className="pp-mini-icon" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 11v2a2 2 0 0 0 2 2h2l4 4V5L7 9H5a2 2 0 0 0-2 2Z" />
      <path d="M16 8a5 5 0 0 1 0 8" />
      <path d="M18.5 5.5a8.5 8.5 0 0 1 0 13" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg viewBox="0 0 24 24" className="pp-mini-icon" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="4" width="18" height="18" rx="3" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  );
}

function BookIcon() {
  return (
    <svg viewBox="0 0 24 24" className="pp-mini-icon" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2Z" />
    </svg>
  );
}

function HeartIcon() {
  return (
    <svg viewBox="0 0 24 24" className="pp-mini-icon" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z" />
    </svg>
  );
}

function ReportIcon() {
  return (
    <svg viewBox="0 0 24 24" className="pp-mini-icon" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 3h6l4 4v12a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h2z" />
      <path d="M9 3v4h6V3" />
      <path d="M8 12h8M8 16h5" />
    </svg>
  );
}

function normalizeTs(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatFeedTime(value) {
  const d = normalizeTs(value);
  if (!d) return "Recently";

  const now = new Date();
  const diffMs = now - d;
  const mins = Math.floor(diffMs / 60000);
  const hours = Math.floor(diffMs / 3600000);
  const days = Math.floor(diffMs / 86400000);

  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;

  return d.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: now.getFullYear() === d.getFullYear() ? undefined : "numeric",
  });
}

function getItemId(item) {
  return String(item?.id || item?.updateId || item?.notificationId || item?.docId || "");
}

function getProfileUid(profile) {
  return String(profile?.uid || profile?.id || profile?.authUid || "");
}

function isAlreadyRead(item) {
  return !!(item?.read || item?.seen || item?.isRead || item?.readAt || item?.seenAt);
}

function normalizeCategory(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
}

function resolveType(item) {
  /*
    Production rule:
    - Category-like fields must win over source/type.
    - A broadcast can still be Attendance, Reports or Behaviour.
    - Only treat source/type "broadcast" as School update when no category exists.
  */
  const directCategory =
    normalizeCategory(item?.parentCategory) ||
    normalizeCategory(item?.notificationCategory) ||
    normalizeCategory(item?.updateCategory) ||
    normalizeCategory(item?.category) ||
    normalizeCategory(item?.module) ||
    normalizeCategory(item?.parentFeedCategory);

  const itemType =
    normalizeCategory(item?.type) ||
    normalizeCategory(item?.kind) ||
    normalizeCategory(item?.notificationType);

  const source = normalizeCategory(item?.source || item?.sourceType);

  const explicit = directCategory || itemType;

  if (
    explicit === "behaviour" ||
    explicit === "behavior" ||
    explicit === "behaviour_log" ||
    explicit === "behavior_log" ||
    explicit === "behaviour_update" ||
    explicit === "behavior_update"
  ) {
    return { key: "behaviour", label: "Behaviour", icon: <HeartIcon /> };
  }

  if (
    explicit === "report" ||
    explicit === "reports" ||
    explicit === "monthly_report" ||
    explicit === "attendance_report"
  ) {
    return { key: "reports", label: "Reports", icon: <ReportIcon /> };
  }

  if (
    explicit === "attendance" ||
    explicit === "attendance_update" ||
    explicit === "absence" ||
    explicit === "absence_report"
  ) {
    return { key: "attendance", label: "Attendance", icon: <CalendarIcon /> };
  }

  if (explicit === "class" || explicit === "class_update") {
    return { key: "class", label: "Class", icon: <BookIcon /> };
  }

  if (
    explicit === "broadcast" ||
    explicit === "announcement" ||
    explicit === "school_update" ||
    explicit === "school_updates" ||
    source === "broadcast"
  ) {
    return { key: "updates", label: "School update", icon: <MegaphoneIcon /> };
  }

  const title = String(item?.title || item?.heading || "").toLowerCase();
  const body = String(item?.body || item?.message || "").toLowerCase();
  const hay = `${title} ${body}`;

  if (hay.includes("report") || hay.includes("pdf") || hay.includes("monthly")) {
    return { key: "reports", label: "Reports", icon: <ReportIcon /> };
  }

  if (hay.includes("attendance") || hay.includes("late") || hay.includes("absent")) {
    return { key: "attendance", label: "Attendance", icon: <CalendarIcon /> };
  }

  if (hay.includes("class")) {
    return { key: "class", label: "Class", icon: <BookIcon /> };
  }

  if (hay.includes("behaviour log") || hay.includes("behavior log")) {
    return { key: "behaviour", label: "Behaviour", icon: <HeartIcon /> };
  }

  return { key: "updates", label: "School update", icon: <MegaphoneIcon /> };
}

function resolveTitle(item) {
  return item?.title || item?.heading || item?.subject || "School update";
}

function resolveBody(item) {
  return item?.body || item?.message || item?.description || item?.content || "Open this item to view more details.";
}

function isPinnedUpdate(item) {
  return Boolean(item?.pinned || item?.isPinned || item?.pinToFeed || item?.pinnedInParentFeed || item?.pinnedToParentFeed || item?.pinToParentFeed || item?.parentPinned);
}

function PinMiniIcon() {
  return (
    <span
      aria-hidden="true"
      style={{
        fontSize: 13,
        lineHeight: 1,
        transform: "translateY(-1px)",
      }}
    >
      📌
    </span>
  );
}

function sortByPinnedThenNewest(a, b) {
  const aPinned = isPinnedUpdate(a) ? 1 : 0;
  const bPinned = isPinnedUpdate(b) ? 1 : 0;

  if (aPinned !== bPinned) return bPinned - aPinned;

  const aTime = normalizeTs(a?.createdAt || a?.publishedAt || a?.updatedAt || a?.date)?.getTime() || 0;
  const bTime = normalizeTs(b?.createdAt || b?.publishedAt || b?.updatedAt || b?.date)?.getTime() || 0;
  return bTime - aTime;
}

function filterLabel(key) {
  const labels = {
    all: "All notifications",
    unread: "Unread only",
    updates: "School updates",
    behaviour: "Behaviour",
    attendance: "Attendance",
    reports: "Reports",
    class: "Class",
  };

  return labels[key] || "All notifications";
}

export default function ParentUpdates() {
  const {
    profile,
    activeChild,
    updates = [],
    unreadCount = 0,
    markAsRead: parentLayoutMarkAsRead,
    markAllAsRead: parentLayoutMarkAllAsRead,
    isOnline = true,
    offlineMessage = "You are offline. Please reconnect and try again.",
  } = useOutletContext() || {};

  const parentUid = getProfileUid(profile);
  const schoolId = profile?.schoolId || activeChild?.schoolId || "";

  const [searchParams] = useSearchParams();
  const requestedFilter = searchParams.get("filter") || "";
  const requestedUpdateId = searchParams.get("updateId") || "";

  const [activeFilter, setActiveFilter] = useState(() => {
    const allowed = new Set(["all", "unread", "updates", "behaviour", "attendance", "reports", "class"]);
    return allowed.has(requestedFilter) ? requestedFilter : "all";
  });
  const [filterOpen, setFilterOpen] = useState(false);
  const filterMenuRef = useRef(null);
  const hasHandledDeepLinkRef = useRef(false);
  const [selected, setSelected] = useState(null);
  const [localReadIds, setLocalReadIds] = useState(() => new Set());
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState("");

  const enrichedUpdates = useMemo(() => {
    return (updates || [])
      .map((item) => {
        const resolved = resolveType(item);
        const id = getItemId(item);

        return {
          ...item,
          id,
          resolvedType: resolved.label,
          resolvedKey: resolved.key,
          resolvedIcon: resolved.icon,
          title: resolveTitle(item),
          body: resolveBody(item),
          time: formatFeedTime(item.createdAt || item.publishedAt || item.updatedAt || item.date),
          isPinned: isPinnedUpdate(item),
          isRead: isAlreadyRead(item) || localReadIds.has(id),
        };
      })
      .filter((item) => item.id)
      .sort(sortByPinnedThenNewest);
  }, [updates, localReadIds]);

  const stats = useMemo(() => {
    const unread = enrichedUpdates.filter((item) => !item.isRead).length;

    return {
      total: enrichedUpdates.length,
      unread,
      updates: enrichedUpdates.filter((item) => item.resolvedKey === "updates").length,
      behaviour: enrichedUpdates.filter((item) => item.resolvedKey === "behaviour").length,
      attendance: enrichedUpdates.filter((item) => item.resolvedKey === "attendance").length,
      reports: enrichedUpdates.filter((item) => item.resolvedKey === "reports").length,
      class: enrichedUpdates.filter((item) => item.resolvedKey === "class").length,
    };
  }, [enrichedUpdates]);

  const filterItems = [
    { key: "all", label: "All notifications", count: stats.total },
    { key: "unread", label: "Unread only", count: stats.unread || unreadCount },
    { key: "updates", label: "School updates", count: stats.updates },
    { key: "behaviour", label: "Behaviour", count: stats.behaviour },
    { key: "attendance", label: "Attendance", count: stats.attendance },
    { key: "reports", label: "Reports", count: stats.reports },
    { key: "class", label: "Class", count: stats.class },
  ];

  const filteredUpdates = useMemo(() => {
    if (activeFilter === "all") return enrichedUpdates;
    if (activeFilter === "unread") return enrichedUpdates.filter((item) => !item.isRead);
    return enrichedUpdates.filter((item) => item.resolvedKey === activeFilter);
  }, [enrichedUpdates, activeFilter]);

  async function markItemAsRead(item) {
    const itemId = getItemId(item);
    if (!itemId) return;

    if (!isOnline) {
      setActionError(offlineMessage);
      return;
    }

    setActionError("");

    setLocalReadIds((prev) => {
      const next = new Set(prev);
      next.add(itemId);
      return next;
    });

    if (activeFilter === "unread") {
      setActiveFilter("all");
    }

    if (typeof parentLayoutMarkAsRead === "function") {
      await parentLayoutMarkAsRead(itemId);
      return;
    }

    if (!parentUid) return;

    try {
      await setDoc(
        doc(db, "users", parentUid, "updateReads", itemId),
        {
          updateId: itemId,
          notificationId: item?.notificationId || itemId,
          type: item?.resolvedKey || item?.type || "update",
          schoolId,
          studentId: item?.studentId || activeChild?.id || activeChild?.studentId || "",
          read: true,
          readAt: serverTimestamp(),
          source: "parent-notification-centre-v3",
        },
        { merge: true }
      );
    } catch (err) {
      console.warn("Failed to mark item as read:", err);
    }
  }

async function handleOpen(item) {
  await hapticSelection();
  setSelected(item);
}

  async function handleMarkAllRead() {
    const unreadItems = enrichedUpdates.filter((item) => !item.isRead);
    if (!unreadItems.length) return;

    if (!isOnline) {
      setActionError(offlineMessage);
      return;
    }

    setActionError("");

    try {
      setSaving(true);

      setLocalReadIds((prev) => {
        const next = new Set(prev);
        unreadItems.forEach((item) => next.add(getItemId(item)));
        return next;
      });

      if (typeof parentLayoutMarkAllAsRead === "function") {
        await parentLayoutMarkAllAsRead();
        return;
      }

      if (!parentUid) return;

      const batch = writeBatch(db);

      unreadItems.forEach((item) => {
        const itemId = getItemId(item);
        if (!itemId) return;

        batch.set(
          doc(db, "users", parentUid, "updateReads", itemId),
          {
            updateId: itemId,
            notificationId: item?.notificationId || itemId,
            type: item?.resolvedKey || item?.type || "update",
            schoolId,
            studentId: item?.studentId || activeChild?.id || activeChild?.studentId || "",
            read: true,
            readAt: serverTimestamp(),
            source: "parent-notification-centre-v3-mark-all",
          },
          { merge: true }
        );
      });

      await batch.commit();
    } catch (err) {
      console.warn("Failed to mark all as read:", err);
    } finally {
      setSaving(false);
    }
  }


  async function handleFilterSelect(nextFilter) {
    await hapticSelection();
    setActiveFilter(nextFilter);
    setFilterOpen(false);
  }

  useEffect(() => {
    if (!filterOpen) return undefined;

    function closeFilter(event) {
      if (filterMenuRef.current && !filterMenuRef.current.contains(event.target)) {
        setFilterOpen(false);
      }
    }

    document.addEventListener("pointerdown", closeFilter);
    return () => document.removeEventListener("pointerdown", closeFilter);
  }, [filterOpen]);


  useEffect(() => {
    const allowed = new Set(["all", "unread", "updates", "behaviour", "attendance", "reports", "class"]);

    if (requestedFilter && allowed.has(requestedFilter)) {
      setActiveFilter(requestedFilter);
    }
  }, [requestedFilter]);

  useEffect(() => {
    if (!requestedUpdateId || hasHandledDeepLinkRef.current || !enrichedUpdates.length) return;

    const target = enrichedUpdates.find((item) => String(item.id) === String(requestedUpdateId));
    if (!target) return;

    hasHandledDeepLinkRef.current = true;
    setActiveFilter(target.resolvedKey || requestedFilter || "all");
    setSelected(target);

    window.setTimeout(() => {
      const safeSelectorValue = String(requestedUpdateId).replace(/"/g, '\\"');
      const el = document.querySelector(`[data-update-id="${safeSelectorValue}"]`);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 180);
  }, [requestedUpdateId, requestedFilter, enrichedUpdates]);


  useEffect(() => {
    if (!selected) return undefined;

    const previousOverflow = document.body.style.overflow;
    const previousTouchAction = document.body.style.touchAction;

    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.touchAction = previousTouchAction;
    };
  }, [selected]);

  return (
    <div className="ppm-page ppm-v3-page ppm-updates-page">
      <section className="ppm-v3-command-card ppm-updates-shell-card">
        <div className="ppm-v3-command-top ppm-updates-hero-row">
          <div>
            <div className="ppm-summary-eyebrow">Notification centre</div>
            <h1 className="ppm-summary-title">All notifications</h1>
            <p className="ppm-summary-text">
              Broadcasts, behaviour updates, attendance notices, and reports for{" "}
              {activeChild?.name || "your child"}.
            </p>
          </div>

          <div className="ppm-v4-unread-badge">
            {stats.unread > 0 ? `${stats.unread} unread` : "Up to date"}
          </div>
        </div>

        <div className="ppm-v4-filter-card ppm-updates-filter-card">
          <div className="ppm-v4-filter-top">
            <div>
              <div className="ppm-summary-eyebrow">View</div>
              <div className="ppm-v4-filter-title">{filterLabel(activeFilter)}</div>
            </div>

            <button
              type="button"
              className="ppm-v3-mark-read-btn"
              onClick={handleMarkAllRead}
              disabled={saving || !stats.unread}
            >
              {saving ? "Updating..." : "Mark all read"}
            </button>
          </div>

          <div className="ppm-filter-select-wrap ppm-filter-menu-wrap" ref={filterMenuRef}>
            <span className="ppm-filter-select-label">Notification type</span>

            <button
              type="button"
              className={`ppm-filter-menu-button ${filterOpen ? "is-open" : ""}`}
              aria-haspopup="listbox"
              aria-expanded={filterOpen}
              onClick={async (event) => {
                event.preventDefault();
                event.stopPropagation();
                await hapticSelection();
                setFilterOpen((current) => !current);
              }}
            >
              <span>
                {filterItems.find((item) => item.key === activeFilter)?.label || "All notifications"}{" "}
                ({filterItems.find((item) => item.key === activeFilter)?.count || 0})
              </span>
              <span className="ppm-filter-menu-chevron">⌄</span>
            </button>

            {filterOpen ? (
              <div className="ppm-filter-menu" role="listbox">
                {filterItems.map((item) => (
                  <button
                    type="button"
                    key={item.key}
                    role="option"
                    aria-selected={activeFilter === item.key}
                    className={`ppm-filter-menu-item ${activeFilter === item.key ? "active" : ""}`}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      handleFilterSelect(item.key);
                    }}
                  >
                    <span>{item.label}</span>
                    <strong>{item.count || 0}</strong>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </div>

        {actionError ? (
          <div className="pp-offline-action-error" role="alert">
            {actionError}
          </div>
        ) : null}

        <div className="ppm-feed-list ppm-v3-feed-list ppm-updates-feed-list">
          {filteredUpdates.length ? (
            filteredUpdates.map((item) => (
              <article
                key={item.id}
                data-update-id={item.id}
                className={`ppm-update-card ppm-v3-update-card ${
                  item.isRead ? "is-read" : "is-unread"
                }`}
              >
                <div className="ppm-update-top">
                  <div className="ppm-update-icon-wrap">{item.resolvedIcon}</div>

                  <div className="ppm-update-copy">
                    <div className="ppm-update-head">
                      <h3>{item.title}</h3>
                      {!item.isRead ? <span className="ppm-notification-dot" /> : null}
                    </div>

 <div className="ppm-meta-row">
  {item.isPinned ? (
    <span className="ppm-pinned-pill">
      <PinMiniIcon />
      Pinned
    </span>
  ) : null}

  <span className="ppm-type-pill">{item.resolvedType}</span>
  <span className="ppm-feed-meta">{item.time}</span>
</div>

                   <p className="ppm-update-preview-text">
  {item.isPinned
    ? "Pinned school notice. Open to read the full message."
    : "Open to read the full message."}
</p>
                  </div>
                </div>

{selected?.id === item.id ? (
  <div className="ppm-inline-update-detail">
    <div className="ppm-inline-update-head">
      <span className="ppm-type-pill">{selected.resolvedType}</span>
      <span className="ppm-feed-meta">{selected.time}</span>
    </div>

    <h4>{selected.title}</h4>

    <p>{selected.body}</p>

    {selected?.studentName || activeChild?.name ? (
      <small>
        Linked child: {selected.studentName || activeChild?.name}
      </small>
    ) : null}
  </div>
) : null}

                <div className="ppm-update-actions">
<button
  type="button"
  className="ppm-modal-primary"
  onClick={() => {
    if (selected?.id === item.id) {
      setSelected(null);
      return;
    }

    handleOpen(item);
  }}
>
  {selected?.id === item.id ? "Close" : "Read update"}
</button>

                  {!item.isRead ? (
                    <button
                      type="button"
                      className="ppm-modal-secondary"
                      onClick={() => markItemAsRead(item)}
                    >
                      Mark read
                    </button>
                  ) : null}
                </div>
              </article>
            ))
          ) : (
            <div className="ppm-empty-state">
              <div className="ppm-empty-icon ppm-empty-icon--small">
                <BellIcon />
              </div>
              <h3>No notifications found</h3>
              <p>There are no matching alerts right now.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}